// 从社区图片详情点过来的自动填充生图参数的弹窗
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import { v4 as UUIDv4 } from 'uuid';
import { Icon } from '@shakkerai/components';
import { getCanvasState } from '@shakkerai/pro-store';
import { getInsertXY, insertImage, isOverseas } from '@shakkerai/pro-utils';
import { applyGenerateParams, switchGlobalMode } from '@starflow/app/modules/generator/helpers';
import { reproduceCollage, reproduceUpscale, reproduceInpaint, reproduceOutpaint } from '@shakkerai/pro-utils';
import { getTempState, setTempState } from '@starflow/app/store';
import { GenerationInfoPanelInner } from '@shakkerai/pro-canvas/src/components/elements/image/info';
import imgSliderHandler from '@shakkerai/pro-canvas/src/assets/slider_handler.png';
import { translate } from '@shakkerai/util-i18n';
import ImgShakkerLogo from '../assets/shakker-logo.svg';
// import ImgPicLogo from '../assets/pic-logo.svg';

const ImgPicLogo =
  'https://liblibai-web-static.liblib.cloud/liblibai_v4_online/static/_next/static/images/pic.017532ad3b2b69c0dc3cd0f2f1c1b412.svg';
const Slider = ({ sourceImageUrl, imageUrl, generateType }) => {
  const refData = useRef({});
  const sliderContainerRef = useRef(null);
  const sliderImageRef = useRef(null);
  const [{ width, height }, setContaierSize] = useState({ width: 0, height: 0 });

  const handleMouseMove = (e) => {
    if (!refData.current.dragging) return;

    const width = sliderContainerRef.current.offsetWidth;
    const dragOffset = e.clientX - sliderImageRef.current.getBoundingClientRect().left;
    const newWidth = Math.min(width, Math.max(0, dragOffset));

    sliderImageRef.current.style.width = `${newWidth}px`;
  };

  const handleMouseDown = () => {
    refData.current.dragging = true;
  };

  useEffect(() => {
    const width = sliderContainerRef.current.offsetWidth;
    const initialWidth = generateType === 'inpaint' ? width * 0.5 : width * 0.8;
    sliderImageRef.current.style.width = `${initialWidth}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateType]);

  useEffect(() => {
    const handleMouseUp = () => {
      refData.current.dragging = false;
    };

    const { offsetWidth: width, offsetHeight: height } = sliderContainerRef.current;
    setContaierSize({ width, height });

    document.body.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={sliderContainerRef} onMouseMove={handleMouseMove} className="flex items-stretch absolute z-10 inset-0">
      <div className="absolute inset-0 overflow-hidden">
        <img
          draggable={false}
          style={{ maxWidth: 'unset', width, height }}
          className="object-contain"
          src={sourceImageUrl}
        />
        <span className="absolute top-2 right-2 px-1.5 py-1 leading-3 bg-black/75 rounded-sm text-xs whitespace-nowrap text-white">
          <span className="translate-y-1">{translate('img_bottom_tool_original_image')}</span>
        </span>
      </div>
      <div ref={sliderImageRef} className="absolute inset-0 bg-[#f7f7f7]">
        <div style={{ height }} className="relative w-full overflow-hidden">
          <img
            draggable={false}
            style={{ maxWidth: 'unset', width, height }}
            className="object-contain"
            src={imageUrl}
          />
        </div>
        <div className="absolute top-0 right-0 bottom-0 flex flex-shrink-0 flex-col items-center justify-center w-px bg-white">
          <img
            draggable={false}
            src={imgSliderHandler}
            onMouseDown={handleMouseDown}
            className="max-w-none w-7 h-7 pointer-events-auto cursor-col-resize"
          />
        </div>
      </div>
    </div>
  );
};

const ReferRemixModal = (props) => {
  const { taskInfo, imageId } = props;

  const authorInfo = taskInfo.authorInfo;
  const genInfo = taskInfo.projectData || taskInfo.taskParam?.projectData || {};
  const image = imageId ? taskInfo.images.find((item) => item.outputId === +imageId) : taskInfo.images[0];

  const sourceImage = useMemo(() => {
    if (!genInfo) return;

    if (genInfo.generateType === 'normal') {
      if (genInfo.imageReference) {
        return genInfo.imageReference;
      }

      const referenceControlnet = genInfo.controlnets?.find((item) => item.type === 'reference' && item.sourceImage);
      return referenceControlnet?.sourceImage;
    } else {
      return genInfo.sourceImage;
    }
  }, [genInfo]);

  const handleImportParams = async () => {
    const tempStates = {};
    const currentMode = getTempState('globalMode');
    const authorAvatar = authorInfo?.avatar;
    const authorNickname = authorInfo?.nickname;

    // 如果图片是自定义模式生的图，但是当前工具是低门槛模式，则自动切换至自定义模式并提示用户
    if (!genInfo.isSimpleMode && currentMode === 'simple') {
      switchGlobalMode('pro');
    }

    if (genInfo.generateType === 'normal') {
      // 如果不是图片工具生的图，直接导入参数到生成器
      applyGenerateParams(genInfo, { styleIcon: image.imageId }, 'all');

      const id = `elementImage${UUIDv4()}`;
      const [x, y] = getInsertXY({ width: image.width, height: image.height, gap: 20 });

      // 将图片插入到画布
      insertImage({
        id,
        url: image.imageId,
        x,
        y,
        width: image.width,
        height: image.height,
        innerImageId: image.outputId,
        innerTaskId: image.generateId,
      });

      // 如果图片有controlnet，则在第一个controlnet位置显示提示
      if (genInfo.controlnets?.length) {
        tempStates.showRemixReferenceReplaceTip = true;
      } else {
        tempStates.showRemixGenerateTip = true;
      }
    } else {
      //如果是图片工具生的图，则需要将原图上传到画布，进入聚焦模式，然后导入参数
      const isInpaint = genInfo.generateType === 'inpaint';
      const isUpscale = genInfo.generateType === 'upscale';
      const isCollage = genInfo.generateType?.startsWith?.('collage');
      const isOutpaint = genInfo.generateType === 'outpaint';

      let remixSourceElement = null;

      if (isInpaint) {
        globalThis.imageToolThumbIndex = 0;
        remixSourceElement = await reproduceInpaint(genInfo);
        globalThis.imageGenerationInfo = genInfo;
        tempStates.showRemixSourceImageReplaceTip = true;
      } else if (isCollage) {
        reproduceCollage(genInfo);
      } else if (isUpscale) {
        globalThis.imageToolThumbIndex = 0;
        remixSourceElement = await reproduceUpscale(genInfo);
        globalThis.imageGenerationInfo = genInfo;
        tempStates.showRemixSourceImageReplaceTip = true;
      } else if (isOutpaint) {
        globalThis.imageToolThumbIndex = 0;
        remixSourceElement = await reproduceOutpaint(genInfo);
        globalThis.imageGenerationInfo = genInfo;
        tempStates.showRemixSourceImageReplaceTip = true;
      }

      if (remixSourceElement) {
        const historyTaskItem = {
          taskId: taskInfo.generateId,
          params: {
            width: image.width,
            height: image.height,
            taskId: taskInfo.generateId,
            projectData: Object.assign({ authorAvatar, authorNickname }, genInfo),
          },
          images: [
            {
              id: image.outputId,
              taskId: image.generateId,
              url: image.imageId,
              seed: image.seed,
              width: image.width,
              height: image.height,
              illegal: image.illegal,
              isNfsw: image.isNfsw,
            },
          ],
          type: genInfo.generateType,
        };

        setTempState({
          focusedThumb: {
            type: 'history',
            data: historyTaskItem.images[0],
            params: historyTaskItem.params,
          },
        });

        getCanvasState().updateElement(
          remixSourceElement.id,
          {
            historyTasks: [historyTaskItem],
          },
          {
            skipTracking: true,
          },
        );
      }
    }

    setTempState(tempStates);
    modals.close('communityImageRemixModal');
  };

  useEffect(() => {
    if (!genInfo || !image) {
      modals.close('communityImageRemixModal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!genInfo || !image) {
    return null;
  }

  // created at: dd/mm/yy hh:mm
  const createdAt = dayjs(taskInfo.createdAt).format(translate('share_remix_time_format'));
  const showSlider = sourceImage;
  const modeString = genInfo?.isSimpleMode ? 'share_remix_simple_mode' : 'share_remix_custom_mode';

  return (
    <div className="flex flex-col items-stretch w-[1000px] tiny-scrollbar h-[788px] max-h-[90vh] min-h-[600px] overflow-hidden bg-white rounded-xl">
      <div className="flex flex-1 items-stretch overflow-hidden">
        <div className="relative flex-1 overflow-hidden bg-[#F7F7F7]">
          {showSlider ? (
            <Slider imageUrl={image.imageId} sourceImageUrl={sourceImage.url} generateType={genInfo.generateType} />
          ) : (
            <img draggable={false} src={image.imageId} className="absolute inset-0 w-full h-full object-contain" />
          )}
        </div>
        <div className="w-[300px] ei overflow-auto tiny-scrollbar py-2">
          <GenerationInfoPanelInner
            image={image}
            genInfo={genInfo}
            withHeader={false}
            withFooter={false}
            withSourceImage={false}
            withSendButtons={false}
            imageWidth={image.width}
            imageHeight={image.height}
          />
        </div>
      </div>
      <footer className="flex items-center justify-between flex-shrink-0 h-[88px] px-6 py-5 bg-[#0C0B10]">
        <div className="flex flex-col">
          <div className="flex items-center">
            <img src={authorInfo?.avatar} className="w-6 h-6 mr-2 bg-white/30 rounded-full object-cover" />
            <span className="text-white text-sm font-semibold">{authorInfo?.nickname}</span>
          </div>
          <div className="flex items-center mt-2">
            <span className="text-base text-white/60 font-bold">{translate('share_remix_creator_prefix')}</span>
            <img className="h-[18px] mx-1" src={isOverseas ? ImgShakkerLogo : ImgPicLogo} alt="" />
            <span className="text-base text-white/60 font-bold">{translate('share_remix_creator_suffix')}</span>
            <div className="w-px h-2.5 mx-3 bg-white/20" />
            <span className="text-base text-white/60 font-bold">{translate(modeString)}</span>
            <div className="w-px h-2.5 mx-3 bg-white/20" />
            <span className="text-base text-white/60 font-bold">{createdAt}</span>
          </div>
        </div>
        <button onClick={handleImportParams} className="ml-[88px] flex-1 h-14 bg-primary rounded-lg text-black">
          <Icon className="icon-border_generate_pic" fontSize={20} />
          <span className="ml-2 text-lg font-semibold font-grotesk">{translate('share_remix_button')}</span>
        </button>
      </footer>
    </div>
  );
};

export const openReferRemixModal = (taskInfo, imageId) => {
  modals.open({
    w: 1000,
    size: 1000,
    zIndex: 5555,
    trapFocus: false,
    modalId: 'communityImageRemixModal',
    centered: true,
    withCloseButton: false,
    closeOnClickOutside: true,
    closeOnEscape: false,
    classNames: {
      overlay: 'bg-black/80',
      header: 'bg-transparent',
      content: 'p-0 bg-transparent tiny-scrollbar',
      body: 'p-0 bg-transparent',
    },
    children: <ReferRemixModal taskInfo={taskInfo} imageId={imageId} />,
  });
};

export const applyPicRemix = (projectData, imageUrl) => {
  // 如果图片是自定义模式生的图，但是当前工具是低门槛模式，则自动切换至自定义模式并提示用户
  if (!projectData.isSimpleMode && getTempState('globalMode') === 'simple') {
    switchGlobalMode('pro');
  }

  // 🈯只生成1张图
  projectData.count = 4;

  // 如果不是图片工具生的图，直接导入参数到生成器
  applyGenerateParams(projectData, { styleIcon: imageUrl }, 'all');
};
